import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/* Material UI */
import { ThemeProvider } from '@material-ui/core/styles';

/* React Router */
import { withRouter, Route } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

/* Redux */
import { compose } from 'redux';
import { connect, Provider } from 'react-redux';

/* Socket IO */
import socketIOClient, { io } from 'socket.io-client';

/* Project */
import { appActions } from 'commons/reducer';
import { actionProps, selectState } from 'utils/reduxActions';
import { ReactReduxContext } from 'config/configureStore';
import connectSaga from 'utils/saga';
import PrivateRoute from 'utils/hooks/PrivateRoute';
import saga from 'commons/saga';
import Login from 'screens/Login';
import { falsy, getItemInStorage, truthty } from 'utils/functions';
import LoadingScreen from 'commons/components/LoadingScreen';
import Logout from 'screens/Logout';
import store from 'index';
import environments from 'config/environments';
import AppRoutes from './routes';
import AppTheme from './theme';

function App(props) {
  // const [isShowingZendeskButton, setIsShowingZendeskButton] = useState(false);
  const { user, actions, loadingUser, role, account, i18n } = props;
  const socketRef = useRef(null);

  useEffect(() => {
    if (falsy(user)) {
      actions.getUser();
    }

    actions.activate();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      actions.getAppStateFromApi();
    }
  }, []);

  useEffect(() => {
    if (truthty(account)) {
      window.document.title = account.companyName || 'Pakit';
      const countryCodeMap = {
        CHILE: 'cl',
        COLOMBIA: 'co',
        ECUADOR: 'ec',
        USA: 'usa',
      };
      const countryKey = (account.country || '').toUpperCase();
      const lang = countryCodeMap[countryKey] || countryKey.toLowerCase() || 'en';

      const savedLanguage = localStorage.getItem('language');
      if (!savedLanguage) {
        i18n.changeLanguage(lang);
        localStorage.setItem('language', lang);
      }
    }
  }, [account]);

  const authSocketPayload = useMemo(() => {
    const userToken = getItemInStorage('user')?.token;
    return { token: userToken };
  }, [user]);

  const webSocketInstance = useCallback(() => {
    const payload = { auth: { token: authSocketPayload.token } };

    return environments.WS_ENDPOINT
      ? socketIOClient(environments.WS_ENDPOINT, payload)
      : io(payload);
  }, [authSocketPayload]);

  useEffect(() => {
    if (user && !socketRef.current) {
      console.log('Creating socket connection...');
      socketRef.current = webSocketInstance();
    }
  }, [user, webSocketInstance]);

  const handleConnect = (socket) => {
    console.log(`Setting socket events for [${socket.id}]:`);

    actions.setSocketId(socket.id);

    socket.emit('[APP] Suscribe');

    if (user && role) {
      const tenant = window.location.hostname.split('.')[0];
      socket.emit('[APP] Subscribe User', {
        userEmail: user.email,
        userRole: role.name,
        tenant,
      });
    }

    socket.removeAllListeners(`id-${socket.id}:[Queue Report]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue Traceability]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue ProcessesExport]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue ReceiptsExport]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue LotsExport]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue Employee Import]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue Client Import]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue Supplier Import]`);
    socket.removeAllListeners(`id-${socket.id}:[Queue Product Import]`);

    socket.on(`id-${socket.id}:[Queue Report]`, (data) => {
      console.info({ queueNotification: data });
      actions.productsReportSocketSuccess({ productsReport: data.result });
      actions.queueNotificationStatus({ status: 'completed', data });
    });

    socket.on(`id-${socket.id}:[Queue Traceability]`, (data) => {
      console.info({ queueNotification: data });
      actions.lotTraceabilitySocketSuccess({ traceabilityReport: data.result });
      actions.queueNotificationStatus({ status: 'completed', data });
    });

    socket.on(`id-${socket.id}:[Queue ProcessesExport]`, (data) => {
      console.info({ queueNotification: data });
      if (data?.error) {
        actions.setError(data.error);
        actions.exportingExcel();
        actions.toggleExportMsg();
        return;
      }
      actions.exportExcelReady(data.result);
      actions.exportingExcel();
    });

    socket.on(`id-${socket.id}:[Queue ReceiptsExport]`, (data) => {
      console.info({ queueNotification: data });
      if (data?.error) {
        actions.setError(data.error);
        actions.exportingExcel();
        actions.toggleExportMsg();
        return;
      }
      actions.exportExcelReady(data.result);
      actions.exportingExcel();
    });

    socket.on(`id-${socket.id}:[Queue LotsExport]`, (data) => {
      console.info({ queueNotification: data });
      if (data?.error) {
        actions.setError(data.error);
        actions.exportingExcel();
        actions.toggleExportMsg();
        return;
      }
      actions.exportExcelReady(data.result);
      actions.exportingExcel();
    });

    // socket.on('[User Subscribed]', (data) => {
    //   console.log('Usuario suscrito para alertas:', data);
    // });

    socket.on('[Balance Alert]', (data) => {
      if (role && role.name === 'admin') {
        actions.addBalanceNotification({
          id: data.data?.id,
          type: data.data?.type,
          title: data.data?.title || 'Balance Erróneo Detectado',
          description: data.data?.description || data.message,
          processId: data.data?.processId,
          tenant: data.tenant,
          timestamp: data.data?.timestamp,
          data: data.data,
        });
      }
    });

    socket.on('[Process Blocked]', (data) => {
      if (role && role.name === 'admin') {
        actions.addBalanceNotification({
          id: data.data?.id,
          type: 'process_blocked',
          title: 'Proceso Bloqueado',
          description: data.message,
          processId: data.data?.processId,
          tenant: data.tenant,
          timestamp: data.timestamp,
          data: data.data,
        });
      }

      socket.on(`id-${socket.id}:[Queue Employee Import]`, (socketData) => {
        console.info({ queueNotification: socketData });

        if (socketData.success) {
          actions.updateImportModal({
            status: 'success',
            message: `${
              socketData.result?.processed || 'Varios'
            } empleados procesados correctamente`,
          });

          actions.importEmployeesSocketSuccess({
            importResult: socketData.result,
          });
          actions.queueNotificationStatus({
            status: 'completed',
            data: socketData,
          });
        } else {
          actions.updateImportModal({
            status: 'error',
            message: socketData.error || socketData.message,
            errors: socketData.errors || [],
          });

          actions.importEmployeesSocketError({
            error: socketData.error || socketData.message,
            errors: socketData.errors,
          });
          actions.queueNotificationStatus({
            status: 'failed',
            data: socketData,
          });
        }
      });

      socket.on(`id-${socket.id}:[Queue Client Import]`, (socketData) => {
        if (socketData.success) {
          actions.updateImportModal({
            status: 'success',
            message: `${
              socketData.result?.processed || 'Varios'
            } clientes procesados correctamente`,
          });

          actions.importClientsSocketSuccess({
            importResult: socketData.result,
          });
          actions.queueNotificationStatus({
            status: 'completed',
            data: socketData,
          });
        } else {
          actions.updateImportModal({
            status: 'error',
            message: socketData.error || socketData.message,
            errors: socketData.errors || [],
          });

          actions.importClientsSocketError({
            error: socketData.error || socketData.message,
            errors: socketData.errors,
          });
          actions.queueNotificationStatus({
            status: 'failed',
            data: socketData,
          });
        }
      });

      socket.on(`id-${socket.id}:[Queue Supplier Import]`, (socketData) => {
        if (socketData.success) {
          actions.updateImportModal({
            status: 'success',
            message: `${
              socketData.result?.processed || 'Varios'
            } proveedores procesados correctamente`,
          });

          actions.importSuppliersSocketSuccess({
            importResult: socketData.result,
          });
          actions.queueNotificationStatus({
            status: 'completed',
            data: socketData,
          });
        } else {
          actions.updateImportModal({
            status: 'error',
            message: socketData.error || socketData.message,
            errors: socketData.errors || [],
          });

          actions.importSuppliersSocketError({
            error: socketData.error || socketData.message,
            errors: socketData.errors,
          });
          actions.queueNotificationStatus({
            status: 'failed',
            data: socketData,
          });
        }
      });

      socket.on(`id-${socket.id}:[Queue Product Import]`, (socketData) => {
        if (socketData.success) {
          actions.updateImportModal({
            status: 'success',
            message: `${
              socketData.result?.processed || 'Varios'
            } productos procesados correctamente`,
          });

          actions.importProductsSocketSuccess({
            importResult: socketData.result,
          });
          actions.queueNotificationStatus({
            status: 'completed',
            data: socketData,
          });
        } else {
          actions.updateImportModal({
            status: 'error',
            message: socketData.error || socketData.message,
            errors: socketData.errors || [],
          });

          actions.importProductsSocketError({
            error: socketData.error || socketData.message,
            errors: socketData.errors,
          });
          actions.queueNotificationStatus({
            status: 'failed',
            data: socketData,
          });
        }
      });
    });

    // socket.on('[Notification]', (data) => {
    //   console.info({ notification: data });
    // });

    socket.on('exception', (exception) => {
      console.log('[Socket] Exception', { exception });
    });

    socket.on('disconnect', () => {
      console.log('[Socket] Disconnected');
    });
  };

  useEffect(() => {
    const socket = socketRef.current;
    console.log('socket instance:', socket);

    socket.on('connect', () => {
      actions.setSocketId(socket.id);
      handleConnect(socket);
    });

    return () => {
      if (socket) {
        console.log('Disconnecting socket on unmount: ', socket);
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  const [zendeskShows, setZendeskShows] = useState(
    window.location.hostname.split('.')[0] === 'demo' ? null : false,
  );
  // const [zendeskUnreadIndicator, setZendeskUnreadIndicator] = useState(0);

  // Function in charge of open and close the message
  const chasqikCreateMessenger = () => {
    if (zendeskShows) {
      // eslint-disable-next-line no-undef
      zE('messenger', 'close');
      setZendeskShows(false);
      // eslint-disable-next-line no-undef
    } else {
      setZendeskShows(true);
      // eslint-disable-next-line no-undef
      zE('messenger', 'open');
      // setZendeskUnreadIndicator(0);
    }
  };

  return loadingUser ? (
    <LoadingScreen />
  ) : (
    <ThemeProvider theme={AppTheme}>
      <Provider store={store}>
        {AppRoutes.map((route) => (
          <PrivateRoute
            key={`router-${route.module}`}
            zendesk={{
              chasqikCreateMessenger,
            }}
            exact
            user={user}
            role={role}
            module={route.module}
            component={route.component}
          />
        ))}
        <Route exact path="/logout" component={Logout} />
        <Route exact path="/" component={Login} />
      </Provider>
    </ThemeProvider>
  );
}

// ------------------ CONFIG CONTAINER ------------------

const withSaga = connectSaga({ key: 'sagaApp', saga });

const withConnect = connect(
  selectState('app.loadingUser', 'app.role', 'app.user', 'app.account'),
  actionProps(appActions),
  null,
  { context: ReactReduxContext },
);

export default compose(withTranslation(), withRouter, withConnect)(withSaga(App));
